import { createContext, startTransition, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { WEB_ROUTES, type CustomerRef, type Lang, type MeResponse, type VerifyResponse } from '@portal-contract';
import { api, setCsrfToken } from '@/api/client';
import { ApiClientError, apiErrorBus, isApiError, isSessionLevelError } from '@/api/errors';
import { useI18n } from '@/i18n/I18nProvider';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous' | 'error';

interface SessionValue {
  status: SessionStatus;
  me: MeResponse | null;
  customers: CustomerRef[];
  /** Why the first GET /api/me failed (offline, 5xx). */
  bootError: ApiClientError | null;
  /** The staff maintenance message, when the kill switch sent us to /unavailable. */
  unavailableMessage: { en: string; es: string } | null;
  retryBoot: () => void;
  /** After POST /auth/code/verify: loads /api/me and settles the language. */
  signIn: (verified: VerifyResponse) => Promise<void>;
  signOut: () => Promise<void>;
  signOutAll: () => Promise<void>;
  /** The visitor used the toggle: remembered locally and, when signed in, saved with PUT /api/preferences. */
  changeLanguage: (lang: Lang) => void;
  /** Routes session-level API errors (expired, signed out, maintenance). Returns true when it did. */
  handleSessionError: (e: unknown) => boolean;
}

const Ctx = createContext<SessionValue | null>(null);

export function defaultDestination(customers: readonly CustomerRef[]): string {
  const only = customers.length === 1 ? customers[0] : undefined;
  return only ? `/a/${encodeURIComponent(only.id)}` : WEB_ROUTES.accounts;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  // useNavigate's identity changes on every location change; the effects below must not re-run for that.
  const routerNavigate = useNavigate();
  const navigateRef = useRef(routerNavigate);
  navigateRef.current = routerNavigate;
  const navigate = useCallback((to: string, opts?: { replace?: boolean }) => navigateRef.current(to, opts), []);
  const queryClient = useQueryClient();
  const i18n = useI18n();
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [me, setMe] = useState<MeResponse | null>(null);
  const [bootError, setBootError] = useState<ApiClientError | null>(null);
  const [unavailableMessage, setUnavailableMessage] = useState<{ en: string; es: string } | null>(null);
  const [bootTick, setBootTick] = useState(0);

  // The latest i18n functions without re-running effects every time the language flips.
  const i18nRef = useRef(i18n);
  i18nRef.current = i18n;

  /**
   * Forgets the session and moves to `to` in ONE transition. Router updates run as transitions (the
   * v7_startTransition flag), so setting the status on its own would render the protected route with no
   * session first and its guard would redirect to /login, overriding the destination.
   */
  const dropSession = useCallback(
    (to: string) => {
      setCsrfToken(null);
      startTransition(() => {
        setMe(null);
        setStatus('anonymous');
        navigate(to, { replace: true });
      });
    },
    [navigate],
  );

  // Cached customer data must not outlive the session; cleared after the protected screens are gone.
  useEffect(() => {
    if (status === 'anonymous') queryClient.clear();
  }, [status, queryClient]);

  const handleSessionError = useCallback(
    (e: unknown): boolean => {
      if (!isSessionLevelError(e)) return false;
      if (e.code === 'portal_unavailable') {
        setUnavailableMessage(e.unavailableMessage ?? null);
        navigate(WEB_ROUTES.unavailable, { replace: true });
      } else if (e.code === 'session_expired') {
        dropSession(WEB_ROUTES.sessionExpired);
      } else {
        dropSession(WEB_ROUTES.login);
      }
      return true;
    },
    [dropSession, navigate],
  );

  useEffect(() => apiErrorBus.subscribe((e) => void handleSessionError(e)), [handleSessionError]);

  // First load: is there a live session? Public screens render while this runs.
  useEffect(() => {
    const ctl = new AbortController();
    setStatus('loading');
    api
      .me(ctl.signal)
      .then((m) => {
        setCsrfToken(m.csrfToken);
        setMe(m);
        setBootError(null);
        setStatus('authenticated');
        if (!i18nRef.current.explicit) i18nRef.current.adoptLang(m.lang);
      })
      .catch((e: unknown) => {
        if (ctl.signal.aborted) return;
        if (isApiError(e, 'unauthenticated', 'session_expired')) {
          setStatus('anonymous');
        } else if (isApiError(e, 'portal_unavailable')) {
          setStatus('anonymous');
          handleSessionError(e);
        } else {
          setBootError(e instanceof ApiClientError ? e : new ApiClientError(0, 'network'));
          setStatus('error');
        }
      });
    return () => ctl.abort();
  }, [bootTick, handleSessionError]);

  const signIn = useCallback(async (verified: VerifyResponse) => {
    setCsrfToken(verified.csrfToken);
    const m = await api.me();
    setCsrfToken(m.csrfToken);
    setMe(m);
    setBootError(null);
    setStatus('authenticated');
    const cur = i18nRef.current;
    if (cur.explicit) {
      // The visitor already chose a language on the login screen: keep it and save it.
      if (m.lang !== cur.lang) void api.setLanguage(cur.lang).catch(() => {});
    } else {
      cur.adoptLang(m.lang);
    }
  }, []);

  const finishSignOut = useCallback(() => dropSession(WEB_ROUTES.signedOut), [dropSession]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch (e) {
      // An already dead session is a successful sign out. Anything else (offline) must not pretend.
      if (!isApiError(e, 'unauthenticated', 'session_expired')) throw e;
    }
    finishSignOut();
  }, [finishSignOut]);

  const signOutAll = useCallback(async () => {
    try {
      await api.logoutAll();
    } catch (e) {
      if (!isApiError(e, 'unauthenticated', 'session_expired')) throw e;
    }
    finishSignOut();
  }, [finishSignOut]);

  const changeLanguage = useCallback(
    (lang: Lang) => {
      i18nRef.current.setLang(lang);
      if (status === 'authenticated') void api.setLanguage(lang).catch((e: unknown) => void handleSessionError(e));
    },
    [status, handleSessionError],
  );

  const value = useMemo<SessionValue>(
    () => ({
      status,
      me,
      customers: me?.customers ?? [],
      bootError,
      unavailableMessage,
      retryBoot: () => setBootTick((n) => n + 1),
      signIn,
      signOut,
      signOutAll,
      changeLanguage,
      handleSessionError,
    }),
    [status, me, bootError, unavailableMessage, signIn, signOut, signOutAll, changeLanguage, handleSessionError],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession must be used inside <SessionProvider>');
  return v;
}
